import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../../../context";

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { useNavigate } from "react-router";
import type { BenchmarkAttributes } from "../../../parse";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  text: {
    marginBottom: theme.spacing(2),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  padding: {
    padding: theme.spacing(2),
  },
}));

export const AnalyticFinish = observer(function AnalyticFinish() {
  const app = useApp();
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const FIELDS: Array<{
    key: keyof BenchmarkAttributes;
    label: string;
    required: boolean;
  }> = [
    { key: "contact_email", label: t("form-email"), required: true },
    { key: "contact_phone", label: t("form-phone"), required: false },
    { key: "contact_company", label: t("form-company"), required: false },
    { key: "contact_name", label: t("form-name"), required: false },
    { key: "contact_position", label: t("form-position"), required: false },
    { key: "contact_street", label: t("form-street"), required: false },
    { key: "contact_post_code", label: t("form-post-code"), required: false },
    { key: "contact_city", label: t("form-city"), required: false },
  ];

  const [emailValid, setEmailValid] = React.useState(() =>
    app.benchmarks.validateCurrentEmail(),
  );

  return (
    <Paper className={classes.padding}>
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12}>
          <div className={classes.formControl}>
            <Typography component="h1" variant="h5">
              {t("contact-info-title")}
            </Typography>

            <Typography variant="body1" className={classes.text}>
              {t("contact-info-description")}
            </Typography>

            <Typography variant="body1" className={classes.text}>
              {t("contact-info-optional")}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" className={classes.formControl}>
            <TextField
              variant="outlined"
              autoFocus={true}
              error={!emailValid}
              required={true}
              fullWidth
              label={FIELDS[0].label}
              defaultValue={app.benchmarks.benchmark?.contact_email}
              onChange={(e) => {
                setEmailValid(
                  app.benchmarks.validateCurrentEmail(e.target.value),
                );
              }}
              onBlur={(e) => {
                setEmailValid(
                  app.benchmarks.validateCurrentEmail(e.target.value),
                );
                app.benchmarks.updateCurrentBenchmark(
                  FIELDS[0].key,
                  e.target.value,
                );
              }}
            />
          </FormControl>

          {FIELDS.filter((_, i) => i !== 0).map((field) => (
            <FormControl
              key={field.key}
              variant="outlined"
              className={classes.formControl}
            >
              <TextField
                variant="outlined"
                required={field.required}
                fullWidth
                label={field.label}
                defaultValue={app.benchmarks.benchmark?.[field.key] as string}
                onBlur={(e) => {
                  app.benchmarks.updateCurrentBenchmark(
                    field.key,
                    e.target.value,
                  );
                }}
              />
            </FormControl>
          ))}

          <FormGroup row className={classes.formControl}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!app.benchmarks.benchmark?.contact_newsletter}
                  onChange={(e) => {
                    app.benchmarks.updateCurrentBenchmark(
                      "contact_newsletter",
                      e.target.checked,
                    );
                  }}
                />
              }
              label={t("newsletter-signup")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!app.benchmarks.benchmark?.contact_get_in_touch}
                  onChange={(e) => {
                    app.benchmarks.updateCurrentBenchmark(
                      "contact_get_in_touch",
                      e.target.checked,
                    );
                  }}
                />
              }
              label={t("contact-permission")}
            />
          </FormGroup>

          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            disabled={!emailValid}
            onClick={() => {
              app.benchmarks.finishCurrentBenchmark().then(() => {
                navigate("/benchmark/finished");
              });
            }}
          >
            Report anfordern
          </Button>

          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate("/benchmark/analytic");
            }}
          >
            Zurück zum Benchmark
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
});
